import React, { useEffect, useState } from 'react';
import discordIntegrationService, { DiscordGuild, DiscordRole } from '../../../services/discordIntegrationService';
import { creatorService } from '../../../services/creatorService';

const DiscordRoleMappingPage: React.FC = () => {
  const [guilds, setGuilds] = useState<DiscordGuild[]>([]);
  const [roles, setRoles] = useState<DiscordRole[]>([]);
  const [selectedGuild, setSelectedGuild] = useState<string>('');
  const [tiers, setTiers] = useState<any[]>([]);
  const [savingTier, setSavingTier] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const guildData = await discordIntegrationService.listGuilds();
        setGuilds(guildData.guilds);
        const myTiers = await creatorService.getMyTiers();
        setTiers(myTiers);
      } catch (e: any) {
        setError(e?.message || 'Failed to load guilds/tiers');
      }
    };
    load();
  }, []);

  const loadRoles = async (guildId: string) => {
    setSelectedGuild(guildId);
    setRoles([]); setError(null);
    try {
      const roleData = await discordIntegrationService.listRoles(guildId);
      // Filter out @everyone role
      setRoles(roleData.roles.filter(r => r.name !== '@everyone'));
    } catch (e: any) {
      setError(e?.message || 'Failed to load roles');
    }
  };

  const toggleRoleForTier = (tierId: string, roleId: string) => {
    setTiers(prev => prev.map(t => {
      if (t.id !== tierId) return t;
      const current: string[] = t.DiscordRoleIds || [];
      const exists = current.includes(roleId);
      return { ...t, DiscordRoleIds: exists ? current.filter(r => r !== roleId) : [...current, roleId] };
    }));
  };

  const saveTier = async (tierId: string) => {
    setSavingTier(tierId); setMessage(null); setError(null);
    const tier = tiers.find(t => t.id === tierId);
    try {
      await discordIntegrationService.saveTierRoleMapping(tierId, tier?.DiscordRoleIds || []);
      setMessage('Saved tier role mapping');
    } catch (e: any) {
      setError(e?.message || 'Failed to save tier');
    } finally {
      setSavingTier(null);
    }
  };

  const syncRoles = async () => {
    setSyncing(true); setMessage(null); setError(null);
    try {
      const resp = await discordIntegrationService.syncRoles();
      setMessage(`Sync complete: assigned ${resp.summary.assigned}, skipped ${resp.summary.skipped}, errors ${resp.summary.errors}`);
    } catch (e: any) {
      setError(e?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    setError(null); setMessage(null);
    try {
      await discordIntegrationService.disconnect();
      setMessage('Discord integration disconnected');
    } catch (e: any) {
      setError(e?.message || 'Failed to disconnect');
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Discord Role Mapping</h1>
      <p className="text-gray-600 mb-4">Map your server roles to subscription tiers to automate access.</p>
      {error && <div className="mb-4 text-red-600 text-sm">{error}</div>}
      {message && <div className="mb-4 text-green-600 text-sm">{message}</div>}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Guild</label>
        <select value={selectedGuild} onChange={e => loadRoles(e.target.value)} className="border rounded px-3 py-2 w-full max-w-md">
          <option value="">-- Choose a guild --</option>
          {guilds.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </div>
      {selectedGuild && roles.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8">
          {tiers.map(t => (
            <div key={t.id} className="border rounded p-4 bg-white shadow-sm">
              <h2 className="font-semibold mb-2">{t.name}</h2>
              <div className="space-y-1 max-h-48 overflow-auto border rounded p-2">
                {roles.map(r => {
                  const active = (t.DiscordRoleIds || []).includes(r.id);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => toggleRoleForTier(t.id, r.id)}
                      className={`flex justify-between w-full text-left text-sm px-2 py-1 rounded ${active ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100'}`}
                    >
                      <span>{r.name}</span>
                      {active && <span>✓</span>}
                    </button>
                  );
                })}
              </div>
              <button disabled={savingTier === t.id} onClick={() => saveTier(t.id)} className="mt-3 text-sm bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded disabled:opacity-50">
                {savingTier === t.id ? 'Saving…' : 'Save Mapping'}
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-3">
        <button onClick={syncRoles} disabled={syncing} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded disabled:opacity-50">
          {syncing ? 'Syncing…' : 'Run Role Sync'}
        </button>
        <button onClick={disconnect} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded">
          Disconnect
        </button>
      </div>
    </div>
  );
};

export default DiscordRoleMappingPage;